import { FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Button as ImgButton } from "common/components";
import { Button } from "common/components-v2";
import Page from "common/components-v2/custom/page/Page";
import SpinnerLoader from "common/components/custom/spinner-loader/SpinnerLoader";
import { ROUTES } from "common/config";
import { ROUTE_QUERY } from "common/config/route.config";
import useCustomTranslation from "common/hooks/useCustomTranslation";
import useDebounce from "common/hooks/useDebounce";
import useProfile from "common/hooks/useProfile";
import { appActions } from "common/redux/app/app.slice";
import { RootState } from "common/redux/core/root.reducer";
import { useGetSuperAdminUserListQuery } from "common/redux/user/queries";
import { getCurrentDateYYYYMMDD } from "common/utils/date";
import { exportStringToCSV } from "common/utils/file";
import commonKeysConfig from "common/config/common-keys.config";
import { ButtonType, ButtonVariant, ToastVariant } from "common/enums";

import { IMAGE_COLLECTION } from "assets/media";
import useBatchLogoPrefetch from "modules/home/hooks/useBatchLogoPrefetch";
import ClientCardSkeleton from "modules/home/components-v2/client-card-skeleton/ClientCardSkeleton";
import ClientCard from "modules/home/components-v2/client-card/ClientCard";
import ClientListItem from "modules/home/components-v2/client-list-item/ClientListItem";
import AddEditClient from "modules/home/components/add-edit-client/AddEditClient";
import ClientListSkeleton from "modules/home/components/client-list-skeleton/ClientListSkeleton";
import CreateEngagement from "modules/home/components/create-engagement/CreateEngagement";
import DisclaimerBanner from "modules/home/components/disclaimer-banner/DisclaimerBanner";
import Welcome from "modules/home/components/welcome/Welcome";
import {
  ClientOptions,
  EngagementCreationSteps
} from "modules/home/enums/create-engagement/create-engagement.enum";
import { Client } from "modules/home/models/home-common.model";
import { homeModuleActions } from "modules/home/redux/home";
import {
  useGetClientListQuery,
  useLazyDownloadFeedbackQuery
} from "modules/home/redux/queries";
import labelCollection from "../../config/langauge-keys.config";

import "./Dashboard.scss";

const Dashboard: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useCustomTranslation(["home", "common"]);
  const { isSuperAdmin } = useSelector((state: RootState) => state.user);
  const scrollDivRef = useRef<HTMLDivElement>(null);
  const dashboardRef = useRef<HTMLDivElement>(null);

  const { isTileViewForClientList } = useSelector(
    (state: RootState) => state.home
  );

  const { givenName } = useProfile();

  const [showAddNewEngagement, setShowAddNewEngagement] = useState(false);
  const [showAddEditClient, setShowAddEditClient] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showRiskAiCreationRetry, setShowRiskAiCreationRetry] = useState(false);
  const [
    downloadingFeedbackClientDetails,
    setDownloadingFeedbackClientDetails
  ] = useState<Client | null>(null);
  const [showTileView, setShowTileView] = useState(isTileViewForClientList);

  // scroll position to add class to dashboard container to show new client btn header in fixed position
  const [scrollPosition, setScrollPosition] = useState(0);
  const debouncedScrollPosition = useDebounce(scrollPosition?.toString(), 25);

  const initialClientDetails = {
    id: "",
    name: "",
    clientCode: "",
    clientIconBase64: "",
    clientIconFileName: "",
    clientIconSasUrl: "",
    sectorCode: "",
    region: ""
  };
  const [currentClientDetails, setCurrentClientDetails] =
    useState<Client>(initialClientDetails);
  const [activeStage, setActiveStage] = useState<EngagementCreationSteps>(
    EngagementCreationSteps.ADD_DETAILS
  );
  const [selectedClientAndEngagement, setSelectedClientAndEngagement] =
    useState<{
      clientId: string;
      engagementId: string;
      isDraftMode?: boolean;
    }>({ clientId: "", engagementId: "", isDraftMode: false });

  const {
    data,
    isFetching: isClientListFetching,
    isLoading: isClientListLoading,
    refetch: refetchClientList
  } = useGetClientListQuery({
    includeAll: true
  });

  useEffect(() => {
    refetchClientList();
  }, []);

  // get scroll position to add class to header
  useEffect(() => {
    const handleScroll = () => {
      const scrollDiv = scrollDivRef?.current;
      if (scrollDiv) {
        setScrollPosition(scrollDiv.scrollTop);
      }
    };

    const scrollDiv = scrollDivRef?.current;
    if (scrollDiv) {
      scrollDiv.addEventListener("scroll", handleScroll);
    }

    // Cleanup the event listener on component unmount
    return () => {
      if (scrollDiv) {
        scrollDiv.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  // add class to dashboard container, to show new client btn header in fixed position
  useEffect(() => {
    const dashboardDiv = dashboardRef?.current;
    const scrollDiv = scrollDivRef?.current;
    const NavTop = dashboardDiv?.getBoundingClientRect().top;
    if (dashboardDiv && scrollDiv) {
      if (
        NavTop !== undefined &&
        Number(debouncedScrollPosition) > NavTop + 340
      ) {
        dashboardDiv.classList.add("active");
      } else {
        dashboardDiv.classList.remove("active");
      }
    }
  }, [debouncedScrollPosition]);

  const { createTileRef } = useBatchLogoPrefetch();

  // fetch super admin user list to check if the user is super admin while adding team members
  useGetSuperAdminUserListQuery();

  const [
    downloadFeedback,
    {
      data: downloadFeedbackData,
      isLoading: isDownloadFeedbackLoading,
      isFetching: isDownloadFeedbackFetching,
      isSuccess: isDownloadFeedbackSuccess,
      isError: isDownloadFeedbackError
    }
  ] = useLazyDownloadFeedbackQuery();

  useEffect(() => {
    if (
      isDownloadFeedbackSuccess &&
      downloadFeedbackData &&
      !isDownloadFeedbackFetching
    ) {
      // download feedback data as CSV
      exportStringToCSV(
        downloadFeedbackData,
        `${
          downloadingFeedbackClientDetails?.name
        }_Feedback_${getCurrentDateYYYYMMDD()}`
      );

      dispatch(
        appActions.setToastDetails({
          isVisible: true,
          message: t(labelCollection.downloadFeedbackSuccessMessage),
          type: ToastVariant.SUCCESS
        })
      );
      setDownloadingFeedbackClientDetails(null);
    } else if (isDownloadFeedbackError) {
      dispatch(
        appActions.setToastDetails({
          isVisible: true,
          message: t(labelCollection.downloadFeedbackErrorMessage),
          type: ToastVariant.ERROR
        })
      );
      setDownloadingFeedbackClientDetails(null);
    }
  }, [
    isDownloadFeedbackFetching,
    isDownloadFeedbackSuccess,
    isDownloadFeedbackError
  ]);

  useEffect(() => {
    if (isTileViewForClientList) {
      setShowTileView(true);
    } else {
      setShowTileView(false);
    }
  }, [isTileViewForClientList]);

  const handleNewEngagement = () => {
    setShowAddNewEngagement(true);
  };

  const handleNewClient = () => {
    setShowAddEditClient(true);
    setIsEditMode(false);
    setCurrentClientDetails(initialClientDetails);
  };

  const handleViewClientDetails = (clientId: string) => {
    navigate(
      ROUTES.HOME.CLIENT_DETAILS.replace(ROUTE_QUERY.CLIENT_ID, clientId)
    );
  };

  const handleClientOptionClick = (option: ClientOptions, data: Client) => {
    switch (option) {
      case ClientOptions.VIEW: {
        if (data?.isRiskAITenantCreationSuccess === false) {
          setCurrentClientDetails(data);
          setShowRiskAiCreationRetry(true);
          setShowAddEditClient(true);
        } else {
          handleViewClientDetails(data.id);
        }
        break;
      }
      case ClientOptions.EDIT: {
        handleEditClient(data);
        break;
      }
      case ClientOptions.DOWNLOAD_FEEDBACK: {
        handleDownloadFeedback(data);
        break;
      }
      default:
        break;
    }
  };

  const handleEditClient = (data: Client) => {
    if (data) {
      setCurrentClientDetails(data);
      setIsEditMode(true);
      setShowRiskAiCreationRetry(false);
      setShowAddEditClient(true);
    }
  };

  const handleDownloadFeedback = (data: Client) => {
    if (data) {
      setDownloadingFeedbackClientDetails(data);
      downloadFeedback({ clientId: data.id });
    }
  };

  const handleModalClose = () => {
    setShowAddNewEngagement(false);
    setShowAddEditClient(false);
    setShowRiskAiCreationRetry(false);
    setIsEditMode(false);
    setActiveStage(EngagementCreationSteps.ADD_DETAILS);
    setCurrentClientDetails(initialClientDetails);
    setSelectedClientAndEngagement({
      clientId: "",
      engagementId: "",
      isDraftMode: false
    });
  };

  const handleListView = () => {
    setShowTileView(false);
    dispatch(homeModuleActions.updateTileViewForClientList(false));
  };

  const handleTileView = () => {
    setShowTileView(true);
    dispatch(homeModuleActions.updateTileViewForClientList(true));
  };

  return (
    <>
      <Page showHeaderAnimation showAnimatedRing>
        <div className="dashboard" ref={dashboardRef}>
          <div className="dashboard__wrapper" ref={scrollDivRef}>
            <div className="welcome-details">
              <div className="motif-h3-default-bold">{`${t(
                labelCollection.WELCOME
              )}, ${givenName}`}</div>
              <h1 className="motif-h2-default-light">
                {t(labelCollection.TITLE)}
              </h1>
              <div className="motif-h6-default-light">
                {t(labelCollection.WELCOME_DESC)}
              </div>

              <div className="action-wrapper">
                <Button
                  onClick={handleNewEngagement}
                  variant="primary"
                  size="medium"
                >
                  <img src={IMAGE_COLLECTION.IC_ADD_WHITE} alt="" />
                  {t(labelCollection.WELCOME_NEW_ENGAGEMENT)}
                </Button>
                {isSuperAdmin && (
                  <Button
                    onClick={handleNewClient}
                    variant="secondary"
                    size="medium"
                  >
                    <img src={IMAGE_COLLECTION.IC_ADD} alt="" />
                    {t(labelCollection.WELCOME_NEW_CLIENT)}
                  </Button>
                )}
              </div>

              {/* Client List */}
              <div className="clients-section">
                <div className="clients-header">
                  <div className="clients-header__left">
                    <h2>
                      <span>{t(labelCollection.clients)}</span>{" "}
                      {data?.length && (
                        <span className="client-count">{data?.length}</span>
                      )}
                    </h2>
                  </div>
                  <div className="clients-header__right">
                    <div className="buttons-wrapper">
                      <ImgButton
                        className={`list-view-button ${
                          showTileView ? "" : "active"
                        }`}
                        type={ButtonType.BUTTON}
                        onClick={handleListView}
                        variant={ButtonVariant.SECONDARY}
                        icon={IMAGE_COLLECTION.IC_LIST_VIEW}
                        aria-label={t(commonKeysConfig.a11yListView)}
                      />
                      <ImgButton
                        className={`tile-view-button ${
                          showTileView ? "active" : ""
                        }`}
                        type={ButtonType.BUTTON}
                        onClick={handleTileView}
                        variant={ButtonVariant.SECONDARY}
                        icon={IMAGE_COLLECTION.IC_TILE_VIEW}
                        aria-label={t(commonKeysConfig.a11yTileView)}
                      />
                    </div>
                  </div>
                </div>

                {showTileView ? (
                  isClientListLoading ? (
                    <div className="clients-tile-view">
                      {Array.from({ length: 8 }).map((_, index) => (
                        <ClientCardSkeleton key={index} />
                      ))}
                    </div>
                  ) : (
                    <div className="clients-tile-view">
                      {data?.map((client) => (
                        <ClientCard
                          key={client.id}
                          ref={
                            client.isClientLogoAvailable
                              ? createTileRef(
                                  client.id,
                                  client.clientIconFileName
                                )
                              : undefined
                          }
                          clientData={client}
                          onOptionClick={handleClientOptionClick}
                        />
                      ))}
                    </div>
                  )
                ) : isClientListLoading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <ClientListSkeleton key={index} />
                  ))
                ) : (
                  <div className="clients-list-view">
                    {data?.map((client) => (
                      <ClientListItem
                        key={client.id}
                        ref={
                          client.isClientLogoAvailable
                            ? createTileRef(
                                client.id,
                                client.clientIconFileName
                              )
                            : undefined
                        }
                        clientData={client}
                        onOptionClick={handleClientOptionClick}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {data?.length === 0 && <Welcome onClick={handleNewClient} />}
      </Page>

      {showAddNewEngagement && (
        <CreateEngagement
          show={showAddNewEngagement}
          defaultStage={activeStage}
          clientId={selectedClientAndEngagement.clientId}
          isDraftMode={selectedClientAndEngagement.isDraftMode}
          onClose={handleModalClose}
        />
      )}

      {showAddEditClient && (
        <AddEditClient
          show={showAddEditClient}
          onClose={() => {
            setShowAddEditClient(false);
            setShowRiskAiCreationRetry(false);
          }}
          isEditMode={isEditMode}
          showRiskAiCreationRetry={showRiskAiCreationRetry}
          clientId={currentClientDetails.id}
        />
      )}

      {((isClientListFetching && !isClientListLoading) ||
        isDownloadFeedbackFetching) && (
        <SpinnerLoader className="client-list--loader" />
      )}

      <DisclaimerBanner />
    </>
  );
};

export default Dashboard;
